import { create } from 'zustand';
import { doDELETE, doGET, doPOST, doPUT } from '../../utils/HttpUtils';
import { showError } from '../toaster';
import { ENDPOINTS } from '../api/constants';
import { Notification, NotificationFilters, NotificationState } from '../../types/notifications';

const MODULE = 'offer';

const store = create<NotificationState>((set, get) => ({
  data: [],
  totalPages: 0,
  total: 0,
  currentPage: 1,
  filters: {},
  isLoading: false,
  rows: 20,

  fetchGrid: async () => {
    try {
      const { filters, currentPage, rows, isLoading } = get();
      if (isLoading) return;

      set({ isLoading: true });

      const queryParams = new URLSearchParams(filters as Record<string, string>);
      queryParams.append('page', String(currentPage));
      queryParams.append('rows', String(rows));
      const apiUrl = `${ENDPOINTS.grid(MODULE)}?${queryParams.toString()}`;

      const response = await doGET(apiUrl);

      if (response.status >= 200 && response.status < 400) {
        const resData = response.data?.data;
        const list = resData?.rows ?? resData ?? [];
        const total = resData?.total ?? (Array.isArray(list) ? list.length : 0);
        set({
          data: Array.isArray(list) ? list : [],
          totalPages: Math.ceil(total / rows) || 1,
          total,
        });
      } else {
        showError(response.message || 'Failed to fetch');
      }
    } catch (err) {
      showError('Failed to fetch');
    } finally {
      set({ isLoading: false });
    }
  },

  setFilters: (newFilters: NotificationFilters) => {
    set({ filters: newFilters, currentPage: 1 });
    get().fetchGrid();
  },

  nextPage: () => {
    const { currentPage, totalPages, fetchGrid } = get();
    if (currentPage < totalPages) {
      set((s) => ({ currentPage: s.currentPage + 1 }));
      fetchGrid();
    }
  },

  prevPage: () => {
    const { currentPage, fetchGrid } = get();
    if (currentPage > 1) {
      set((s) => ({ currentPage: s.currentPage - 1 }));
      fetchGrid();
    }
  },

  onPageChange: (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    const { totalPages, fetchGrid } = get();
    const newPage = page + 1;
    if (newPage >= 1 && newPage <= totalPages) {
      set({ currentPage: newPage });
      fetchGrid();
    }
  },

  onCreate: async (data: any) => {
    try {
      const response = await doPOST(ENDPOINTS.create(MODULE), data);
      if (response.status >= 200 && response.status < 400) {
        get().fetchGrid();
        return response?.data?.data ?? null;
      }
      return null;
    } catch {
      return null;
    }
  },

  onUpdate: async (data: Notification) => {
    try {
      const response = await doPUT(ENDPOINTS.update(MODULE), data);
      if (response.status >= 200 && response.status < 400) {
        get().fetchGrid();
        return response?.data?.data ?? null;
      }
      return null;
    } catch {
      return null;
    }
  },

  onDelete: async (id: string) => {
    try {
      const response = await doDELETE(ENDPOINTS.delete(MODULE, id));
      if (response.status >= 200 && response.status < 400) {
        get().fetchGrid();
      }
    } catch {
      // ignore
    }
  },

  detail: async (id: string) => {
    try {
      const response = await doGET(ENDPOINTS.detail(MODULE, id));
      return response?.data ?? { data: null };
    } catch {
      return { data: null };
    }
  },
}));

export const usePushNotificationStore = () => store.getState();
